import { InboxOutlined } from '@ant-design/icons';
import { Col, Form, Input, InputNumber, Row, Upload, UploadFile } from 'antd';
import { UploadChangeParam } from 'antd/es/upload';
import Dragger from 'antd/es/upload/Dragger';
import { skillLevyApi } from 'Api/skillLevy';
import Buttons from 'components/Global/Buttons';
import DatePickerField from 'components/Global/DatePicker';
import dayjs from 'dayjs';
import { FC, useState } from 'react';
import { formatToDateOnly, invalidText, toastText } from 'utils/utils';
import styles from './index.module.scss';
import './index.scss';
import { SideDrawerBodyProps } from './types';

const AddSkillLevySideDrawer: FC<SideDrawerBodyProps> = (props) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [date, setDate] = useState<any>(dayjs());
	const [file, setFile] = useState<any>(null);
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [grossPercentage, setGrossPercentage] = useState<number | null>(null);

	let isRemoving = false;

	const [form] = Form.useForm();

	const {
		closeDrawerByAnimation,
		fetchSkillLevyData,
		fetchAllSkillLevyData,
	} = props;

	const handleDate = (value: any) => {
		setDate(value);
	};

	const buttons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: true,
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			disabled: loading,
			onclick: () => {
				closeDrawerByAnimation();
			},
		},
		{
			text: 'Save',
			isLoading: loading,
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			className: 'btn-blue',
			isSubmit: true,
			disabled: false,
			onclick: () => {},
			font: '16px',
		},
	];

	const propsUpload = {
		name: 'file',
		maxCount: 1,
		fileList: fileList,
		beforeUpload: (file: UploadFile) => {
			if (!file) {
				return;
			}

			const isLt1M = file.size! / 1024 / 1024 < 5;
			if (!isLt1M) {
				toastText('File must be smaller than 5MB!', 'error');
				return Upload.LIST_IGNORE;
			}

			setFile(file);
			setFileList([file]);
			return false; // Prevents automatic upload
		},
		onChange(info: UploadChangeParam<UploadFile>) {
			const { file } = info;
			if (!isRemoving) {
				setFile(file);
				setFileList([file]);
			} else {
				isRemoving = false;
				setFile(null);
				setFileList([]);
			}
		},
		onDrop: () => {
			setFile(null);
			setFileList([]);
		},
		onRemove: () => {
			isRemoving = true;
		},
	};

	const handleSubmit = async (values: any) => {
		setLoading(true);
		const skillLevyData = new FormData();

		skillLevyData.append(
			'effectiveDate',
			formatToDateOnly(values.effectiveDate)
		);
		skillLevyData.append('grossPercentage', values.grossPercentage);
		skillLevyData.append(
			'notes',
			invalidText(values.notes) ? null : values.notes
		);
		skillLevyData.append('moduleName', 'SKILL_LEVY');
		skillLevyData.append('file', file as Blob);

		try {
			const response = await skillLevyApi.createSkillLevy(skillLevyData);
		
			let message =
				response?.data?.message ||
				'Skill levy details saved successfully';
			toastText(message, 'success');
			closeDrawerByAnimation(true);
			fetchSkillLevyData();
			fetchAllSkillLevyData();
		} catch (error: any) {
			let message =
				error?.response?.data?.message ||
				`Failed to save Skill Levy details`;
			toastText(message, 'error');
		} finally {
			setLoading(false);
		}
	};

	const validatePercentage = (_: any, value: number) => {
		if (value < 0 || value > 100) {
			return Promise.reject(new Error('Percentage must be between 0 and 100'));
		}
		return Promise.resolve();
	};

	return (
			<div className={styles['side-drawer-body']}>
				<Form
					name="basic"
					className={styles['side-drawer-form']}
					form={form}
					labelCol={{ span: 8 }}
					wrapperCol={{ span: 24 }}
					style={{ maxWidth: 600 }}
					onFinish={handleSubmit}
					initialValues={{ effectiveDate: date }}
					autoComplete="off"
				>
					<p className={styles['form-container-head-warning']}>
						<b>
							{' '}
							<sup>*</sup>
						</b>{' '}
						Indicated mandatory fields
					</p>
					<div className={styles['side-drawer-form__inputs']}>
						<label
							className={styles['side-drawer-form__napsa--label']}
						>
							{'Effective Date'}{' '}
							<span className="required-color">*</span>
						</label>
						<div>
							<Form.Item
								name="effectiveDate"
								rules={[
									{
										required: true,
										message: 'Please select a date',
									},
								]}
							>
								<DatePickerField
									onChange={handleDate}
									value={date}
									isError={false}
									disabled={false}
									placeholder="Select Date"
									required
									name="effectiveDate"
									// disabledBeforeDates={dayjs().startOf('day')}
								/>
							</Form.Item>
						</div>
					</div>

					<Row>
						<Col span={24} className="input-column">
							<div className={styles['side-drawer-form__inputs']}>
								<label
									className={
										styles['side-drawer-form__napsa--label']
									}
								>
									{'Gross Percentage (%)'}{' '}
									<span className="required-color">*</span>
								</label>
								<Form.Item
									name="grossPercentage"
									rules={[
										{
											required: true,
											message:
												'Please enter Gross percentage',
										},
										{ validator: validatePercentage },
									]}
								>
									<div>
										<InputNumber
											value={grossPercentage}
											onChange={setGrossPercentage}
											addonAfter="%"
											size="large"
											min={0}
											max={100}
											step={0.01}
											parser={(value: any) => {
												const parsedValue = parseFloat(
													value?.replace('%', '') ||
														'0'
												);
												return isNaN(parsedValue)
													? 0
													: parsedValue;
											}}
											style={{ width: '100%' }}
										/>
										<span className={styles['helper-text']}>
											Percentage of total gross emoluments
										</span>
									</div>
								</Form.Item>
							</div>
						</Col>
					</Row>

					<div className={styles['side-drawer-form__inputs']}>
						<label
							className={styles['side-drawer-form__napsa--label']}
						>
							{'Attachment'}
						</label>
						<Form.Item name="attachment">
							<Dragger {...propsUpload} className="test">
								<p className="ant-upload-drag-icon">
									<InboxOutlined />
								</p>
								<p className="ant-upload-hint">
									Drag and drop a file or choose file from
									device.
									<br />
									<span className="color-purple">
										Maximum file size: 5MB
									</span>
								</p>
							</Dragger>
						</Form.Item>
					</div>

					<div className={styles['side-drawer-form__inputs']}>
						<label
							className={styles['side-drawer-form__napsa--label']}
						>
							{'Notes'}
						</label>
						<Form.Item name="notes" wrapperCol={{ span: 24 }}>
							<Input.TextArea
								className={styles['side-drawer-form--textarea']}
							/>
						</Form.Item>
					</div>

					<div className={styles['side-drawer-form__buttons']}>
						<Form.Item>
							<Buttons buttons={buttons} />
						</Form.Item>
					</div>
				</Form>
			</div>
	);
};

export default AddSkillLevySideDrawer;
